import { Lap } from '../domain/lap/Lap'
import { WeatherCondition } from '../api/apiTypes'
import { getWeatherLabel } from '../domain/weather/weatherFormatting'

/**
 * Summary of a collection of laps — rider count, sensor availability,
 * weather distribution. Used in both shared-lap and multi-session UIs.
 */
export interface LapCollectionSummary {
  /** Number of unique riders/drivers */
  riderCount: number
  /** Human-readable sensor summary, e.g. "All laps: GPS + IMU" */
  sensorSummary: string
  /** Human-readable weather summary, e.g. "All Dry" or "2 Dry, 1 Wet" */
  weatherSummary: string
  /** The most common weather condition */
  dominantWeather: WeatherCondition
}

/**
 * Compute a summary from a collection of domain Laps.
 * Pure function — no side effects, no UI dependencies.
 */
export function summarizeLapCollection(laps: Lap[]): LapCollectionSummary {
  if (laps.length === 0) {
    return {
      riderCount: 0,
      sensorSummary: 'No laps',
      weatherSummary: 'Unknown conditions',
      dominantWeather: WeatherCondition.Unknown,
    }
  }

  const uniqueDrivers = new Set(laps.map((l) => l.driverName))
  const weatherCounts = new Map<WeatherCondition, number>()
  let withGyro = 0
  let withAccel = 0

  laps.forEach((lap) => {
    const condition = lap.weather?.condition ?? WeatherCondition.Unknown
    weatherCounts.set(condition, (weatherCounts.get(condition) ?? 0) + 1)
    if (lap.gyro && lap.gyro.length > 0) withGyro++
    if (lap.accl && lap.accl.length > 0) withAccel++
  })

  const total = laps.length
  const hasIMU = withGyro > 0 || withAccel > 0
  let sensorSummary: string
  if (withGyro === total && withAccel === total) {
    sensorSummary = 'All laps: GPS + IMU'
  } else if (hasIMU) {
    const imuCount = Math.max(withGyro, withAccel)
    sensorSummary = `${imuCount} with IMU, ${total - imuCount} GPS only`
  } else {
    sensorSummary = 'All laps: GPS only'
  }

  const weatherEntries = Array.from(weatherCounts.entries()).filter(
    ([c]) => c !== WeatherCondition.Unknown
  )
  let weatherSummary: string
  if (weatherEntries.length === 0) {
    weatherSummary = 'Unknown conditions'
  } else if (weatherEntries.length === 1) {
    weatherSummary = `All ${getWeatherLabel(weatherEntries[0][0])}`
  } else {
    weatherSummary = weatherEntries
      .map(([c, n]) => `${n} ${getWeatherLabel(c)}`)
      .join(', ')
  }

  const dominantWeather =
    weatherEntries.length > 0
      ? weatherEntries.reduce((a, b) => (a[1] >= b[1] ? a : b))[0]
      : WeatherCondition.Unknown

  return {
    riderCount: uniqueDrivers.size,
    sensorSummary,
    weatherSummary,
    dominantWeather,
  }
}
